import React, { Component } from 'react';
import PropTypes from 'prop-types';
import AutoCompleteTooltip from './AutoCompleteTooltip';
import Chip from 'components/ui/chip/Chip';
import FieldTitle from 'components/aor/FieldTitle';
import { AutoComplete } from 'material-ui';
import apiClient from 'clients/apiClient';
import config from 'helpers/config';
import styled from 'styled-components';
import translate from 'components/hoc/translate';
import uniq from 'lodash/uniq';
import utils from 'helpers/utils';

const StyledMultiAutoCompleteFieldWrapper = styled.div`
    width: ${ (props) => {
        return props.fullWidth ? '100%' : 'auto';
    } };
`;

const StyledChipWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;

    &:last-child {
        margin-bottom: 10px;
    }
`;

class MultiAutoCompleteField extends Component {
    constructor(props) {
        super(props);

        this.state = {
            dataSource: [],
            error: '',
            loading: false,
            selectedValues: [],
            searchText: '',
        };
    }

    componentDidMount() {
        const {
            input: {
                value,
            },
        } = this.props;

        if (!utils.isEmpty(value)) {
            this.fetchPrefilledSelectedValues();
        }
    }

    setLoading = (loading) => {
        this.setState({
            loading: loading,
        });
    }

    fetchData = (params) => {
        params = params || {};

        const {
            reference,
            searchParams,
        } = this.props;

        let newParams = { ...params, searchParams };

        const url = `${ config.getApiBaseUrl() }/${ reference }`;

        return apiClient.read(url, newParams).then((response) => {
            return response;
        }, (error) => {
            setTimeout(() => {
                this.setLoading(false);

                this.setState({
                    error: error.response.message,
                });
            }, 2000);

            return;
        });
    }

    fetchPrefilledSelectedValues = () => {
        const {
            input: {
                value,
            },
            optionValue,
            searchBy,
            searchParams,
        } = this.props;

        const params = {
            [optionValue]: value,
            searchParams,
        };

        this.setLoading(true);

        return this.fetchData(params).then((response) => {
            this.setState({
                selectedValues: response.results.map((choice) => {
                    return {
                        text: choice[searchBy],
                        value: choice[optionValue],
                    };
                }),
                loading: false,
            });
        });
    }

    // Called on text-input-field changes.
    onUpdateInput = utils.debounce((searchText, dataSource, params) => {
        const {
            minLength,
            optionValue,
            searchBy,
            translate,
        } = this.props;

        // If the string of the source is not 'change' it will not continue.
        if (params.source !== 'change') {
            return;
        }

        // Remove whitespaces around the text.
        if (utils.isString(searchText)) {
            searchText.trim();
        }

        // Remove whitespaces around the text.
        if (searchText.length < minLength) {
            return this.setState({
                dataSource: [],
                loading: false,
                error: '',
            });
        }

        this.setState({
            searchText,
        });

        this.setLoading(true);

        this.fetchData({
            [searchBy]: `*${ searchText }*`,
        }).then((response) => {
            let error;

            if (utils.isEmpty(response.results)) {
                error = translate('general.noMatches');
            }

            setTimeout(() => {
                this.setLoading(false);

                this.setState({
                    dataSource: response.results.map((choice) => {
                        return {
                            text: choice[searchBy],
                            value: choice[optionValue],
                        };
                    }),
                    error: error,
                });
            }, 2000);
        });
    }, 500);

    // Is called when user has selected an option in popover
    handleNewRequest = (dataSource) => {
        const {
            input: {
                onChange,
                value = [],
            },
        } = this.props;

        const updatedValue = uniq([
            ...value,
            dataSource.value,
        ]);

        this.setState((state) => {
            return {
                searchText: '',
                selectedValues: uniq([
                    ...state.selectedValues,
                    dataSource,
                ]),
            };
        }, () => {
            onChange(updatedValue);
        });
    };

    handleDelete = (event) => {
        const { id } = event.target;

        const {
            input: {
                onChange,
                value = [],
            },
        } = this.props;

        let updatedValue = [
            ...value,
        ];

        const index = value.findIndex((value) => {
            return value == id;
        });

        updatedValue.splice(index, 1);

        if (utils.isEmpty(updatedValue)) {
            updatedValue = null;
        }

        this.setState((state) => {
            return {
                selectedValues: state.selectedValues.filter((selectedValue) => {
                    return selectedValue.value != id;
                }),
            };
        }, () => {
            onChange(updatedValue);
        });
    }

    onBlur = (event) => {
        const { onBlur, value } = this.props.input;

        /**
         * We only want to trigger blur this selector when the input value has changed to an empty string.
         * In all the other cases, we want keep the typed data in the input field.
         */
        if (event.target.value === '' && !value.length) {
            onBlur(null);
        }
    }

    render() {
        const {
            dataSource,
            error,
            loading,
            selectedValues,
        } = this.state;

        const {
            animated,
            floatingLabelFixed,
            fullWidth,
            input: {
                name,
                value,
            },
            label,
            meta: {
                touched,
            },
            translate,
        } = this.props;

        return (
            <StyledMultiAutoCompleteFieldWrapper>
                {
                    (!!value.length) && (
                        <StyledChipWrapper>
                            {
                                value.map((value) => {
                                    const entity = selectedValues.find((entity) => {
                                        return entity.value == value;
                                    }) || null;

                                    if (!entity) {
                                        return null;
                                    }

                                    return (
                                        <Chip
                                            key={ value }
                                            style={ { cursor: 'pointer' } }
                                            id={ value }
                                            onRequestDelete={ (event) => {
                                                event.target.id = value;

                                                return this.handleDelete(event);
                                            } }
                                        >
                                            <span>{ entity.text }</span>
                                        </Chip>
                                    );
                                })
                            }
                        </StyledChipWrapper>
                    )
                }

                <div>
                    <AutoComplete
                        animated={ animated }
                        dataSource={ dataSource }
                        errorText={ touched && error }
                        filter={ AutoComplete.noFilter }
                        fullWidth={ fullWidth }
                        floatingLabelFixed={ floatingLabelFixed }
                        floatingLabelText={ label ? <FieldTitle
                            label={ label }
                            source={ 'source' }
                            resource={ 'resource' }
                        /> : '' }
                        id={ name }
                        onBlur={ this.onBlur }
                        onNewRequest={ this.handleNewRequest }
                        onUpdateInput={ (searchText, dataSource, params) => {
                            this.onUpdateInput(searchText, dataSource, params);
                        } }
                        hintText={ translate('components.autocomplete.hinttext') }
                        menuStyle={ {
                            maxHeight: '500px',
                            width: '100%',
                            overflowY: 'auto',
                            touchAction: 'none',
                        } }
                        openOnFocus={ true }
                        searchText={ this.state.searchText }
                    />

                    { (error || loading) &&
                        <AutoCompleteTooltip error={ error } loading={ loading } />
                    }
                </div>
            </StyledMultiAutoCompleteFieldWrapper>
        );
    }
}

MultiAutoCompleteField.defaultProps = {
    minLength: 3,
    optionValue: 'id',
    searchParams: {},
};

MultiAutoCompleteField.propTypes = {
    animated: PropTypes.bool,
    floatingLabelFixed: PropTypes.bool,
    fullWidth: PropTypes.bool,
    input: PropTypes.shape({
        name: PropTypes.string.isRequired,
        onBlur: PropTypes.func.isRequired,
        onChange: PropTypes.func.isRequired,
        value: PropTypes.node,
    }).isRequired,
    label: PropTypes.string,
    meta: PropTypes.shape({
        touched: PropTypes.bool,
    }).isRequired,
    minLength: PropTypes.number,
    optionValue: PropTypes.string,
    reference: PropTypes.string.isRequired,
    searchBy: PropTypes.string.isRequired,
    searchParams: PropTypes.object,
    translate: PropTypes.func.isRequired,
};

export default translate(MultiAutoCompleteField);


// WEBPACK FOOTER //
// ./src/components/list/Filters/types/autocomplete/MultiAutoCompleteField.js